import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { components } from '@/types/api.generated';
import { EXTERNAL_URLS } from '@/constants/config';
import { formatTime } from '@lib/datetime';
import type { DeliveryFlowWire } from '../board/boardWire';
import { summarizeRouteEta } from '../board/etaSignals';
import { useEnRouteSummary } from '../hooks/useEnRouteSummary';
import { buildEnRouteRows, enRouteTimes, type EnRouteRow } from './enRouteModel';
import { StopDots } from './StopDots';

type DispatchRouteEtaReport = components['schemas']['DispatchRouteEtaReport'];

export interface EnRoutePaneProps {
  date: string;
  isToday: boolean;
  flow: DeliveryFlowWire | undefined;
  /** The page's `/api/dispatch/eta` query data (same key), today only. */
  eta: DispatchRouteEtaReport | undefined;
}

const CARD = 'rounded-xl bg-surface-1 ring-1 ring-inset ring-border/60';
const DASH = '—';
const MINUTE_MS = 60_000;

function Kpi({
  testId,
  label,
  value,
  valueClass = '',
  sub,
}: {
  testId: string;
  label: string;
  value: string;
  valueClass?: string;
  sub: string;
}) {
  return (
    <div data-testid={testId} className={`${CARD} px-4 py-3.5`}>
      <div className="text-[12.5px] text-muted-foreground">{label}</div>
      <div
        className={`text-[26px] font-display font-bold leading-[1.15] tracking-[-0.02em] tabular-nums ${valueClass}`}
      >
        {value}
      </div>
      <div className="text-[12.5px] text-muted-foreground">{sub}</div>
    </div>
  );
}

/**
 * The row's latest ETA: muted text, a warning tag with the minutes past the
 * window end when late, a dash without an ETA.
 */
function EtaCell({ row, windowEndMs }: { row: EnRouteRow; windowEndMs: number | null }) {
  const { t } = useTranslation();
  const testId = `dispatch-enroute-eta-${row.key}`;
  if (row.etaMs === null) {
    return (
      <span
        data-testid={testId}
        data-late="false"
        className="justify-self-end whitespace-nowrap text-[12.5px] text-muted-foreground"
      >
        {DASH}
      </span>
    );
  }
  const etaDone = t('dispatch.board.copy.etaDone', { at: formatTime(row.etaMs) });
  if (row.late && windowEndMs !== null) {
    const count = Math.ceil((row.etaMs - windowEndMs) / MINUTE_MS);
    return (
      <span
        data-testid={testId}
        data-late="true"
        className="justify-self-end whitespace-nowrap rounded-sm bg-warning/10 px-2 text-[11.5px] font-medium leading-[22px] text-foreground tabular-nums"
      >
        {`${etaDone} · ${t('dispatch.pane.enRoute.lateBy', { count })}`}
      </span>
    );
  }
  return (
    <span
      data-testid={testId}
      data-late="false"
      className="justify-self-end whitespace-nowrap text-[12.5px] text-muted-foreground tabular-nums"
    >
      {etaDone}
    </span>
  );
}

/**
 * The en-route pane: four KPI cards, one stop-progress row per driver on the
 * road and a read-only note. Rows and totals come from
 * `GET /api/dispatch/en-route` (D1); today's ETAs are the page's existing ETA
 * query, and the late count is the board's own `summarizeRouteEta`. Pure read:
 * the Onfleet link is a plain `<a>`.
 */
export function EnRoutePane({ date, isToday, flow, eta }: EnRoutePaneProps) {
  const { t } = useTranslation();
  const summary = useEnRouteSummary(date, isToday);

  const times = flow ? enRouteTimes(flow, date) : null;
  const windowEnd = times?.windowEnd ?? null;
  const windowEndMs = times?.windowEndMs ?? null;
  const late =
    isToday && eta?.date === date && windowEndMs !== null
      ? summarizeRouteEta(eta, windowEndMs).late
      : null;

  const data = summary.data;
  // Onfleet ETAs are today's only, as for the late KPI.
  const rows = data ? buildEnRouteRows(data, isToday ? eta : undefined, windowEndMs) : [];

  let body: ReactNode;
  if (summary.isError) {
    body = <p className="text-[13px] text-destructive">{t('dispatch.pane.enRoute.loadFailed')}</p>;
  } else if (!data) {
    body = (
      <>
        <div className="h-[88px] animate-pulse rounded-xl bg-muted" />
        <div className="h-[120px] animate-pulse rounded-xl bg-muted" />
      </>
    );
  } else {
    const enRoute = data.drivers.reduce((sum, driver) => sum + driver.en_route, 0);
    const drivers = data.drivers.filter(
      (driver) => (driver.driver_id ?? driver.onfleet_worker_id) !== null,
    ).length;
    body = (
      <>
        <div className="grid grid-cols-4 gap-2.5 max-[980px]:grid-cols-2">
          <Kpi
            testId="dispatch-enroute-kpi-delivered"
            label={t('dispatch.pane.enRoute.kpi.delivered')}
            value={String(data.totals.delivered)}
            valueClass="text-foreground"
            sub={t('dispatch.pane.enRoute.kpi.deliveredOf', { count: data.totals.due })}
          />
          <Kpi
            testId="dispatch-enroute-kpi-enroute"
            label={t('dispatch.board.stage.en_route')}
            value={String(enRoute)}
            sub={t('dispatch.pane.enRoute.kpi.drivers', { count: drivers })}
          />
          <Kpi
            testId="dispatch-enroute-kpi-late"
            label={t('dispatch.pane.enRoute.kpi.lateTitle', { time: windowEnd ?? DASH })}
            value={late === null ? DASH : t('dispatch.pane.enRoute.kpi.lateValue', { count: late })}
            sub={
              isToday
                ? t('dispatch.pane.enRoute.kpi.byOnfleetEta')
                : t('dispatch.pane.enRoute.etaTodayOnly')
            }
          />
          <Kpi
            testId="dispatch-enroute-kpi-failed"
            label={t('dispatch.pane.enRoute.kpi.failed')}
            value={String(data.totals.exception)}
            sub={t('dispatch.pane.enRoute.kpi.byJobStatus')}
          />
        </div>
        {/* One grid owns the columns, so the auto-width ones line up across rows. */}
        <div
          className={`${CARD} grid grid-cols-[minmax(0,148px)_auto_minmax(0,1fr)_auto] gap-x-4 px-4 py-1.5 max-[980px]:grid-cols-[minmax(0,100px)_auto_minmax(0,1fr)_auto]`}
        >
          {rows.map((row) => (
            <div
              key={row.key}
              data-testid={`dispatch-enroute-row-${row.key}`}
              className="col-span-4 grid h-[38px] grid-cols-subgrid items-center"
            >
              <span className="flex min-w-0 items-center gap-2 text-[13px]">
                <span
                  data-testid="dispatch-enroute-swatch"
                  className="h-2.5 w-2.5 shrink-0 rounded-micro"
                  style={{ backgroundColor: row.color }}
                />
                <span className="truncate">
                  {row.driverName ?? t('dispatch.pane.enRoute.unknownDriver')}
                </span>
              </span>
              <span className="whitespace-nowrap text-[12.5px] text-muted-foreground tabular-nums">
                {t('dispatch.pane.enRoute.stops', {
                  done: row.completed,
                  count: row.completed + row.enRoute,
                })}
              </span>
              <StopDots row={row} />
              <EtaCell row={row} windowEndMs={windowEndMs} />
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <div data-testid="dispatch-pane-en_route" className="flex flex-col gap-4">
      {body}
      <div className={`${CARD} px-4 py-5 text-[13px] text-muted-foreground`}>
        {t('dispatch.pane.enRoute.readOnlyBefore')}{' '}
        <a
          href={EXTERNAL_URLS.onfleetDashboard}
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent underline underline-offset-2"
        >
          {t('dispatch.pane.enRoute.openOnfleet')}
        </a>
        {t('dispatch.pane.enRoute.readOnlyAfter')}
      </div>
    </div>
  );
}
